import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Pencil, Plus, Trash2, Users, User } from "lucide-react";

import PilotageEmptyState from "@/components/pilotage/PilotageEmptyState";
import PilotageField from "@/components/pilotage/PilotageField";
import PilotageModal from "@/components/pilotage/PilotageModal";
import PilotageTable from "@/components/pilotage/PilotageTable";
import AppErrorState from "@/components/ui/AppErrorState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { getApiErrorMessage } from "@/lib/api";
import {
  COLLABORATOR_STATUSES,
  CONTRACT_KINDS,
  createCollaborator,
  deleteCollaborator,
  listCollaborators,
  updateCollaborator,
} from "@/lib/pilotageApi";
import {
  calculateSeniority,
  getResourceTypeLabel,
  getContractKindLabel,
  getCollaboratorStatusLabel,
  formatDateLabel,
} from "@/lib/pilotageUi";

const COLLABORATOR_STATUS_FILTERS = [
  { value: "all", label: "Tous" },
  { value: "ACTIVE", label: "Actif" },
  { value: "NOTICE", label: "Préavis" },
  { value: "PLANNED", label: "Planifié" },
  { value: "LEFT", label: "Sorti" },
  { value: "INACTIVE", label: "Inactif" },
  { value: "RECRUITMENT", label: "Recrutement" },
];

const normalizeSearchText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const toInputDate = (value) => {
  if (!value) {
    return "";
  }

  return String(value).slice(0, 10);
};

const EMPTY_FORM = {
  full_name: "",
  email: "",
  status: "ACTIVE",
  contract_kind: "FREELANCE",
  internal_cost_monthly: "",
  employment_start_date: "",
  employment_end_date: "",
};

const CollaboratorsPage = () => {
  const { role } = useAuth();
  const isReadOnly = role === "RH";

  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCollaborator, setEditingCollaborator] = useState(null);
  const [formValues, setFormValues] = useState(EMPTY_FORM);
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    contractKind: "all",
  });
  const showEmptyState = !loading && collaborators.length === 0;

  const loadCollaborators = async () => {
    setLoading(true);
    setError("");

    try {
      const data = await listCollaborators();
      setCollaborators(data);
    } catch (requestError) {
      setError(
        getApiErrorMessage(requestError, "Impossible de charger les collaborateurs."),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadCollaborators();
  }, []);

  const contractKindOptions = useMemo(() => {
    const contractKinds = [
      ...CONTRACT_KINDS,
      ...collaborators.map((collaborator) => collaborator?.contract_kind).filter(Boolean),
    ];

    return Array.from(new Set(contractKinds)).map((contractKind) => ({
      value: contractKind,
      label: getContractKindLabel(contractKind),
    }));
  }, [collaborators]);

  const filteredCollaborators = useMemo(() => {
    const query = normalizeSearchText(filters.search);

    return collaborators.filter((collaborator) => {
      const status = String(collaborator.status || "").toUpperCase();
      const contractKind = String(collaborator.contract_kind || "").toUpperCase();

      if (filters.status !== "all" && status !== filters.status) {
        return false;
      }
      if (filters.contractKind !== "all" && contractKind !== filters.contractKind) {
        return false;
      }
      if (!query) {
        return true;
      }

      const searchValues = [
        collaborator.full_name,
        collaborator.email,
        collaborator.status,
        getCollaboratorStatusLabel(collaborator.status),
        collaborator.contract_kind,
        getContractKindLabel(collaborator.contract_kind),
      ];

      return normalizeSearchText(searchValues.join(" ")).includes(query);
    });
  }, [collaborators, filters]);

  const resetFilters = () => {
    setFilters({
      search: "",
      status: "all",
      contractKind: "all",
    });
  };

  const openCreateModal = () => {
    setEditingCollaborator(null);
    setFormValues(EMPTY_FORM);
    setModalOpen(true);
  };

  const openEditModal = (collaborator) => {
    setEditingCollaborator(collaborator);
    setFormValues({
      full_name: collaborator.full_name || "",
      email: collaborator.email || "",
      status: collaborator.status || "ACTIVE",
      contract_kind: collaborator.contract_kind || "FREELANCE",
      internal_cost_monthly: collaborator.internal_cost_monthly !== null && collaborator.internal_cost_monthly !== undefined ? String(collaborator.internal_cost_monthly) : "",
      employment_start_date: toInputDate(collaborator.employment_start_date),
      employment_end_date: toInputDate(collaborator.employment_end_date || collaborator.exit_date),
    });
    setModalOpen(true);
  };

  const closeModal = (nextOpen) => {
    setModalOpen(nextOpen);
    if (!nextOpen) {
      setEditingCollaborator(null);
      setFormValues(EMPTY_FORM);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");

    const payload = {
      full_name: formValues.full_name.trim(),
      email: formValues.email.trim(),
      status: formValues.status,
      contract_kind: formValues.contract_kind,
      internal_cost_monthly: formValues.internal_cost_monthly !== "" ? parseFloat(formValues.internal_cost_monthly) : null,
      employment_start_date: formValues.employment_start_date || null,
      employment_end_date: formValues.employment_end_date || null,
    };

    try {
      if (editingCollaborator) {
        await updateCollaborator(editingCollaborator.id, payload);
      } else {
        await createCollaborator(payload);
      }

      closeModal(false);
      await loadCollaborators();
    } catch (requestError) {
      if (requestError?.response?.status === 409) {
        import("react-hot-toast").then(({ toast }) => {
          toast.error(
            <div>
              Un collaborateur existe déjà pour cette adresse email.
            </div>,
            { duration: 5000 }
          );
        });
        setError("Un collaborateur existe déjà avec cet email.");
      } else {
        setError(
          getApiErrorMessage(requestError, "Impossible d'enregistrer le collaborateur."),
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (collaborator) => {
    const shouldDelete = window.confirm(
      `Supprimer le collaborateur "${collaborator.full_name}" ?`,
    );

    if (!shouldDelete) {
      return;
    }

    setError("");

    try {
      await deleteCollaborator(collaborator.id);
      await loadCollaborators();
    } catch (requestError) {
      setError(
        getApiErrorMessage(requestError, "Impossible de supprimer le collaborateur."),
      );
    }
  };

  return (
    <div className="space-y-6">
      <section className="rounded-3xl border border-white/10 glass-heavy p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              Pilotage
            </p>
            <h1 className="mt-2 text-2xl font-heading font-bold text-white">
              Collaborateurs
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-400">
              Référentiel des consultants. Les RH peuvent consulter les profils,
              tandis que l&apos;administration conserve le CRUD complet.
            </p>
          </div>
          {!isReadOnly && !showEmptyState ? (
            <Button type="button" onClick={openCreateModal} className="btn-primary">
              <Plus className="h-4 w-4" />
              Créer collaborateur
            </Button>
          ) : null}
        </div>
      </section>

      {error ? <AppErrorState message={error} /> : null}

      {!showEmptyState ? (
        <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[minmax(260px,1.5fr)_repeat(2,minmax(160px,1fr))_auto]">
            <Input
              type="search"
              value={filters.search}
              onChange={(event) =>
                setFilters((current) => ({ ...current, search: event.target.value }))
              }
              placeholder="Rechercher un collaborateur..."
              className="bg-slate-950/80"
            />
            <select
              aria-label="Statut collaborateur"
              value={filters.status}
              onChange={(event) =>
                setFilters((current) => ({ ...current, status: event.target.value }))
              }
              className="flex h-9 w-full rounded-md border border-white/10 bg-slate-950 px-3 py-1 text-sm text-white focus:outline-none focus:ring-1 focus:ring-lenvia-blue"
            >
              {COLLABORATOR_STATUS_FILTERS.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
            <select
              aria-label="Type de contrat"
              value={filters.contractKind}
              onChange={(event) =>
                setFilters((current) => ({ ...current, contractKind: event.target.value }))
              }
              className="flex h-9 w-full rounded-md border border-white/10 bg-slate-950 px-3 py-1 text-sm text-white focus:outline-none focus:ring-1 focus:ring-lenvia-blue"
            >
              <option value="all">Tous</option>
              {contractKindOptions.map((contractKind) => (
                <option key={contractKind.value} value={contractKind.value}>
                  {contractKind.label}
                </option>
              ))}
            </select>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={resetFilters}
              className="h-9 whitespace-nowrap border-white/10 text-slate-200 hover:bg-white/5 hover:text-white"
            >
              Réinitialiser les filtres
            </Button>
          </div>
          <p className="mt-3 text-xs font-medium text-slate-400">
            {filteredCollaborators.length} collaborateurs affichés / {collaborators.length} au total
          </p>
        </section>
      ) : null}

      {showEmptyState ? (
        <PilotageEmptyState
          icon={Users}
          title="Aucun collaborateur"
          description="Ajoutez un premier collaborateur pour pouvoir l'affecter aux missions de pilotage."
          actionLabel="Créer collaborateur"
          onAction={openCreateModal}
          isReadOnly={isReadOnly}
          testId="collaborators-empty-state"
        />
      ) : (
        <PilotageTable
          columns={[
            {
              key: "full_name",
              label: "Nom",
              render: (collaborator) => (
                <div className="flex items-center gap-2">
                  <Link
                    to={`/admin/pilotage/collaborateurs/${collaborator.id}`}
                    className="block hover:bg-white/5 -mx-2 px-2 py-1 rounded transition-colors group"
                  >
                    <p className="font-medium text-white group-hover:text-lenvia-blue transition-colors">
                      {collaborator.full_name}
                    </p>
                    <p className="mt-1 text-xs text-slate-500 group-hover:text-lenvia-blue/70 transition-colors">
                      {collaborator.id}
                    </p>
                  </Link>
                  {collaborator.candidate_id && (
                    <Link
                      to={`/admin/recrutement/candidats/${collaborator.candidate_id}`}
                      className="text-violet-400 hover:text-violet-300 transition-colors"
                      title="Voir la fiche candidat"
                    >
                      <User className="h-4 w-4" />
                    </Link>
                  )}
                </div>
              ),
            },
            {
              key: "email",
              label: "Email",
              render: (collaborator) => collaborator.email,
            },
            {
              key: "status",
              label: "Statut",
              render: (collaborator) => {
                const status = collaborator.status || "ACTIVE";
                const label = getCollaboratorStatusLabel(status);
                let colorClass = "bg-slate-400/10 text-slate-400 ring-slate-400/20";
                if (status === "ACTIVE") {
                  colorClass = "bg-emerald-400/10 text-emerald-400 ring-emerald-400/20";
                } else if (status === "RECRUITMENT") {
                  colorClass = "bg-blue-400/10 text-blue-400 ring-blue-400/20";
                } else if (status === "PLANNED") {
                  colorClass = "bg-amber-400/10 text-amber-400 ring-amber-400/20";
                } else if (status === "NOTICE") {
                  colorClass = "bg-orange-400/10 text-orange-400 ring-orange-400/20";
                }
                return (
                  <span
                    className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${colorClass}`}
                  >
                    {label}
                  </span>
                );
              },
            },
            {
              key: "contract_kind",
              label: "Contrat",
              render: (collaborator) => {
                const kind = collaborator.contract_kind;
                const resourceLabel = getResourceTypeLabel(kind);
                const kindLabel = getContractKindLabel(kind);
                return (
                  <div className="flex flex-col">
                    <span className="text-white text-sm">{kindLabel}</span>
                    <span className="text-slate-500 text-xs mt-0.5">{resourceLabel}</span>
                  </div>
                );
              },
            },
            {
              key: "employment_start_date",
              label: "Entrée",
              render: (collaborator) => formatDateLabel(collaborator.employment_start_date),
            },
            {
              key: "employment_end_date",
              label: "Sortie",
              render: (collaborator) => {
                const endDate = collaborator.employment_end_date || collaborator.exit_date;
                if (!endDate) {
                  return (
                    <div className="flex items-center gap-1.5 text-slate-400">
                      <span>—</span>
                      {collaborator.status === "ACTIVE" && (
                        <span
                          className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400"
                          title="Collaborateur actuellement présent"
                        />
                      )}
                    </div>
                  );
                }
                return formatDateLabel(endDate);
              },
            },
            {
              key: "seniority",
              label: "Ancienneté",
              render: (collaborator) =>
                calculateSeniority(
                  collaborator.employment_start_date,
                  collaborator.employment_end_date,
                ),
            },
          ]}
          rows={filteredCollaborators}
          loading={loading}
          emptyMessage="Aucun collaborateur ne correspond aux filtres."
          renderActions={
            isReadOnly
              ? null
              : (collaborator) => (
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => openEditModal(collaborator)}
                      className="border-white/10 text-slate-200 hover:bg-white/5 hover:text-white"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      Modifier
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(collaborator)}
                      className="border-red-500/20 text-red-200 hover:bg-red-500/10 hover:text-red-100"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Supprimer
                    </Button>
                  </div>
                )
          }
        />
      )}

      {!isReadOnly ? (
        <PilotageModal
          open={modalOpen}
          onOpenChange={closeModal}
          title={
            editingCollaborator
              ? "Modifier le collaborateur"
              : "Créer un collaborateur"
          }
          description="Le collaborateur pourra ensuite être affecté à une ou plusieurs missions."
          onSubmit={handleSubmit}
          submitLabel={editingCollaborator ? "Enregistrer" : "Créer"}
          isSubmitting={saving}
        >
          <div className="grid gap-4 md:grid-cols-2">
            <PilotageField label="Nom complet" htmlFor="collaborator-name">
              <Input
                id="collaborator-name"
                value={formValues.full_name}
                onChange={(event) =>
                  setFormValues((current) => ({
                    ...current,
                    full_name: event.target.value,
                  }))
                }
                placeholder="Prénom Nom"
                required
              />
            </PilotageField>
            <PilotageField label="Email" htmlFor="collaborator-email">
              <Input
                id="collaborator-email"
                type="email"
                value={formValues.email}
                onChange={(event) =>
                  setFormValues((current) => ({
                    ...current,
                    email: event.target.value,
                  }))
                }
                placeholder="consultant@lenvia.eu"
                required
              />
            </PilotageField>
            <PilotageField label="Statut" htmlFor="collaborator-status">
              <select
                id="collaborator-status"
                value={formValues.status}
                onChange={(event) => {
                  const nextStatus = event.target.value;
                  setFormValues((current) => {
                    const nextValues = { ...current, status: nextStatus };
                    if (["ACTIVE", "RECRUITMENT", "PLANNED"].includes(nextStatus)) {
                      nextValues.employment_end_date = "";
                    }
                    return nextValues;
                  });
                }}
                className="flex h-9 w-full rounded-md border border-white/10 bg-slate-950 px-3 py-1 text-sm text-white focus:outline-none focus:ring-1 focus:ring-lenvia-blue"
              >
                {COLLABORATOR_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {getCollaboratorStatusLabel(status)}
                  </option>
                ))}
              </select>
            </PilotageField>
            <PilotageField label="Type de contrat" htmlFor="collaborator-contract-kind">
              <select
                id="collaborator-contract-kind"
                value={formValues.contract_kind}
                onChange={(event) =>
                  setFormValues((current) => ({
                    ...current,
                    contract_kind: event.target.value,
                  }))
                }
                className="flex h-9 w-full rounded-md border border-white/10 bg-slate-950 px-3 py-1 text-sm text-white focus:outline-none focus:ring-1 focus:ring-lenvia-blue"
              >
                {CONTRACT_KINDS.map((contractKind) => (
                  <option key={contractKind} value={contractKind}>
                    {getContractKindLabel(contractKind)}
                  </option>
                ))}
              </select>
            </PilotageField>
            <PilotageField label="Date d'entrée" htmlFor="collaborator-start-date">
              <Input
                id="collaborator-start-date"
                type="date"
                value={formValues.employment_start_date}
                onChange={(event) =>
                  setFormValues((current) => ({
                    ...current,
                    employment_start_date: event.target.value,
                  }))
                }
                required
              />
            </PilotageField>
            <PilotageField label="Date de sortie" htmlFor="collaborator-end-date">
              <Input
                id="collaborator-end-date"
                type="date"
                value={formValues.employment_end_date}
                onChange={(event) =>
                  setFormValues((current) => ({
                    ...current,
                    employment_end_date: event.target.value,
                  }))
                }
                disabled={["ACTIVE", "RECRUITMENT", "PLANNED"].includes(formValues.status)}
                required={["LEFT", "INACTIVE"].includes(formValues.status)}
              />
            </PilotageField>
          </div>
        </PilotageModal>
      ) : null}
    </div>
  );
};

export default CollaboratorsPage;
